import { X } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef } from "react";
import { SignIn } from "./sign-in/SignIn";

export const SignInModal: React.FC<{
  open: boolean;
  setOpen: (open: boolean) => void;
}> = ({ open, setOpen }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Sync open state with native dialog
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Handle native close (Escape key, etc.)
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => setOpen(false);
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, [setOpen]);

  // Close on backdrop click
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      if (e.target === dialogRef.current) {
        setOpen(false);
      }
    },
    [setOpen],
  );

  return (
    // biome-ignore lint: click on backdrop to dismiss
    <dialog
      ref={dialogRef}
      onClick={handleClick}
      className="
        w-full max-w-md p-0 m-auto
        bg-transparent
        backdrop:bg-black/50 backdrop:backdrop-blur-sm
        open:animate-modal-in
      "
    >
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden relative">
        {/* Close button */}
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 z-10 flex items-center justify-center size-8 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <X className="size-4" />
        </button>

        <div className="p-8 pt-10">
          {open && (
            <SignIn
              onComplete={() => {
                setOpen(false);
                window.location.reload();
              }}
            />
          )}
        </div>
      </div>
    </dialog>
  );
};
